/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React, { useEffect, useMemo, useState } from "react";
import { gql, useApolloClient, useQuery } from "@apollo/client";
import { FileInfo, FileType, Folder } from "Api";
import {
  FiChevronDown,
  FiChevronRight,
  FiFile,
  FiFolder,
} from "react-icons/fi";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";
import { getTooltipOverrides } from "../../DesignSystem/TooltipStyles";
import { handleApolloError } from "../../Shared/Errors";
import listAllFoldersGql from "../../Api/Gql/ListAllFolders";
import filesInFolderGql from "../../Api/Gql/FilesInFolder";
import { SUPPORTED_FILE_TYPES } from "../../Shared/SupportedFileTypes";
import { UUID } from "../../Utils/Types";

export const DEFAULT_INPUT_PANEL_WIDTH = 280;

// Each level of the tree is indented by this many pixels.
const INDENT_PX = 16;

const TOOLTIP_ENTER_DELAY = 1000;

// Key used for files that live at the root (no folder).
const ROOT_KEY = "__root__";

type DexterTheme = ReturnType<typeof useDexterTheme>["currentTheme"];

interface InputsSidePanelProps {
  show: boolean;
  panelWidth?: number;
}

/** A single file in the tree. fileType gates dragging and feeds the canvas filter. */
interface FileLeaf {
  fileId: UUID;
  fileName: string;
  fileType: string;
}

/** A folder and everything directly under it. */
interface FolderTreeNode {
  id: UUID;
  name: string;
  folders: FolderTreeNode[];
  files: FileLeaf[];
}

const fileLeafOf = (file: FileInfo): FileLeaf => ({
  fileId: file.id,
  fileName: file.attributes?.name ?? "(unnamed)",
  // Match what the engine actually requires: it parses by the user-confirmed
  // type, so a file with no user-selected type can't become an input node.
  // Presenting it by its predicted type would let it be dragged, then fail.
  fileType: file.userSelectedFileType ?? "UNDETECTED",
});

/** Nest the flat folder list by parentId and hang each folder's files off it. */
function buildFolderTree(
  folders: Folder[],
  filesByFolder: Record<string, FileLeaf[]>
): { rootFolders: FolderTreeNode[]; rootFiles: FileLeaf[] } {
  const nodes = new Map<string, FolderTreeNode>();

  folders.forEach((folder) =>
    nodes.set(folder.id, {
      id: folder.id,
      name: folder.name,
      folders: [],
      files: filesByFolder[folder.id] ?? [],
    })
  );

  const rootFolders: FolderTreeNode[] = [];
  folders.forEach((folder) => {
    const node = nodes.get(folder.id);
    if (!node) return;
    const parent = folder.parentId ? nodes.get(folder.parentId) : undefined;
    if (parent) {
      parent.folders.push(node);
    } else {
      rootFolders.push(node);
    }
  });

  const byName = (a: { name: string }, b: { name: string }): number =>
    a.name.localeCompare(b.name);
  const sortTree = (node: FolderTreeNode): void => {
    node.folders.sort(byName);
    node.folders.forEach(sortTree);
  };

  rootFolders.sort(byName);
  rootFolders.forEach(sortTree);

  return { rootFolders, rootFiles: filesByFolder[ROOT_KEY] ?? [] };
}

/** Every file at or beneath this folder, depth-first. */
function collectDescendantFiles(node: FolderTreeNode): FileLeaf[] {
  return [...node.files, ...node.folders.flatMap(collectDescendantFiles)];
}

/**
 * Every row id at or beneath this folder, including the folder itself: its own
 * id, each descendant folder's id, and every descendant file's id. Used to
 * highlight the whole subtree a folder-drag would pull in.
 */
function collectSubtreeIds(node: FolderTreeNode): string[] {
  const ids: string[] = [node.id, ...node.files.map((f) => f.fileId)];
  node.folders.forEach((child) => ids.push(...collectSubtreeIds(child)));
  return ids;
}

function FileRow({
  file,
  depth,
  theme,
  highlightedIds,
  setHighlight,
}: {
  file: FileLeaf;
  depth: number;
  theme: DexterTheme;
  highlightedIds: Set<string>;
  setHighlight: (ids: Set<string>) => void;
}): JSX.Element {
  const supported = SUPPORTED_FILE_TYPES.includes(file.fileType as FileType);
  const highlighted = highlightedIds.has(file.fileId);
  const highlightColor =
    theme.colors.dropdownItemHoverBackground ??
    theme.colors.selectionBackgroundPrimary;

  return (
    <StatefulTooltip
      placement={PLACEMENT.top}
      onMouseEnterDelay={TOOLTIP_ENTER_DELAY}
      content={
        supported
          ? file.fileName
          : "This file type is not yet supported by the parser"
      }
      overrides={getTooltipOverrides(theme)}
    >
      {/* Anchor wrapper: baseui attaches the tooltip's hover handlers to this
          element, so the row below keeps its own highlight handlers. */}
      <div>
        <div
          draggable={supported}
          onDragStart={
            supported
              ? (e) => {
                  e.dataTransfer.setData(
                    "application/inputfile",
                    JSON.stringify({
                      fileId: file.fileId,
                      fileName: file.fileName,
                    })
                  );
                  e.dataTransfer.effectAllowed = "move";
                }
              : undefined
          }
          onMouseEnter={() => setHighlight(new Set([file.fileId]))}
          onMouseLeave={() => setHighlight(new Set())}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 12px",
            paddingLeft: 12 + depth * INDENT_PX,
            minHeight: 34,
            boxSizing: "border-box",
            borderRadius: 4,
            backgroundColor: highlighted ? highlightColor : "transparent",
            cursor: supported ? "grab" : "not-allowed",
            opacity: supported ? 1 : 0.5,
            color: theme.colors.text,
            fontFamily: theme.fonts.primary,
            fontSize: theme.fontSizes.large,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <FiFile style={{ flexShrink: 0, opacity: 0.7 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {file.fileName}
          </span>
        </div>
      </div>
    </StatefulTooltip>
  );
}

function FolderRow({
  node,
  depth,
  theme,
  highlightedIds,
  setHighlight,
}: {
  node: FolderTreeNode;
  depth: number;
  theme: DexterTheme;
  highlightedIds: Set<string>;
  setHighlight: (ids: Set<string>) => void;
}): JSX.Element {
  // Folders start collapsed.
  const [open, setOpen] = useState(false);
  const highlighted = highlightedIds.has(node.id);
  const highlightColor =
    theme.colors.dropdownItemHoverBackground ??
    theme.colors.selectionBackgroundPrimary;

  const handleDragStart = (e: React.DragEvent): void => {
    const files = collectDescendantFiles(node);
    e.dataTransfer.setData(
      "application/inputfolder",
      JSON.stringify({
        fileNames: files.map((f) => f.fileName),
        fileIds: files.map((f) => f.fileId),
        fileTypes: files.map((f) => f.fileType),
      })
    );
    e.dataTransfer.effectAllowed = "move";
  };

  return (
    <>
      <StatefulTooltip
        placement={PLACEMENT.top}
        onMouseEnterDelay={TOOLTIP_ENTER_DELAY}
        content={`Drag to add every supported file in "${node.name}"`}
        overrides={getTooltipOverrides(theme)}
      >
        {/* Anchor wrapper: baseui attaches the tooltip's hover handlers here,
            so the folder header below keeps its own highlight handlers. */}
        <div>
          <div
            draggable
            onDragStart={handleDragStart}
            onClick={() => setOpen((prev) => !prev)}
            onMouseEnter={() => setHighlight(new Set(collectSubtreeIds(node)))}
            onMouseLeave={() => setHighlight(new Set())}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              paddingLeft: 12 + depth * INDENT_PX,
              minHeight: 34,
              boxSizing: "border-box",
              borderRadius: 4,
              backgroundColor: highlighted ? highlightColor : "transparent",
              cursor: "pointer",
              userSelect: "none",
              color: theme.colors.text,
              fontFamily: theme.fonts.primary,
              fontSize: theme.fontSizes.large,
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {open ? (
              <FiChevronDown style={{ flexShrink: 0 }} />
            ) : (
              <FiChevronRight style={{ flexShrink: 0 }} />
            )}
            <FiFolder
              style={{
                flexShrink: 0,
                color:
                  theme.colors.collectionAccentColor ?? theme.colors.primary,
              }}
            />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {node.name}
            </span>
          </div>
        </div>
      </StatefulTooltip>
      {open && (
        <>
          {node.folders.map((child) => (
            <FolderRow
              key={child.id}
              node={child}
              depth={depth + 1}
              theme={theme}
              highlightedIds={highlightedIds}
              setHighlight={setHighlight}
            />
          ))}
          {node.files.map((file) => (
            <FileRow
              key={file.fileId}
              file={file}
              depth={depth + 1}
              theme={theme}
              highlightedIds={highlightedIds}
              setHighlight={setHighlight}
            />
          ))}
        </>
      )}
    </>
  );
}

/**
 * Inputs side panel: a compact tree of the user's folders and files.
 *
 * Drag a file onto the canvas to create an input node for it; drag a folder to
 * create input nodes for every supported file found anywhere beneath it. The
 * canvas (TransformEngine.onDrop) is what turns these drops into nodes — this
 * panel only emits the drag payloads ("application/inputfile" for a file,
 * "application/inputfolder" for a folder's flattened file list).
 *
 * Hovering a file highlights that row; hovering a folder highlights the whole
 * subtree it would drag (itself plus every descendant folder and file).
 */
const InputsSidePanel: React.FC<InputsSidePanelProps> = ({
  show,
  panelWidth = DEFAULT_INPUT_PANEL_WIDTH,
}) => {
  const { currentTheme } = useDexterTheme();
  const apolloClient = useApolloClient();

  const [filesByFolder, setFilesByFolder] = useState<
    Record<string, FileLeaf[]>
  >({});

  // Ids of the rows currently highlighted on hover (a single file, or a folder
  // and all of its descendants).
  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set());

  const { data: foldersData } = useQuery(gql(listAllFoldersGql), {
    fetchPolicy: "network-only",
    onError: (error) => handleApolloError(error, "Error loading folders"),
  });

  const folders: Folder[] = useMemo(
    () => (foldersData?.listAllFolders as Folder[]) ?? [],
    [foldersData]
  );

  // Once we know the folders, load every folder's files (plus the root's) so a
  // folder drag can gather its whole subtree synchronously.
  useEffect(() => {
    if (!foldersData) return;
    let cancelled = false;

    const targets: (UUID | null)[] = [null, ...folders.map((f) => f.id)];
    Promise.all(
      targets.map((folderId) =>
        apolloClient
          .query({
            query: gql(filesInFolderGql),
            variables: { folderId },
            fetchPolicy: "network-only",
          })
          .then((result) => ({
            key: folderId ?? ROOT_KEY,
            files: ((result.data?.filesInFolder as FileInfo[]) ?? []).map(
              fileLeafOf
            ),
          }))
          .catch(() => ({ key: folderId ?? ROOT_KEY, files: [] as FileLeaf[] }))
      )
    ).then((results) => {
      if (cancelled) return;
      const map: Record<string, FileLeaf[]> = {};
      results.forEach(({ key, files }) => {
        map[key] = files;
      });
      setFilesByFolder(map);
    });

    return () => {
      cancelled = true;
    };
  }, [foldersData, folders, apolloClient]);

  const { rootFolders, rootFiles } = useMemo(
    () => buildFolderTree(folders, filesByFolder),
    [folders, filesByFolder]
  );

  const isEmpty = rootFolders.length === 0 && rootFiles.length === 0;

  return (
    <div
      style={{
        width: panelWidth,
        background: currentTheme.colors.background,
        transition: "transform 0.4s cubic-bezier(0.4,0,0.2,1)",
        transform: show ? "translateX(0)" : `translateX(-${panelWidth}px)`,
        display: "flex",
        flexDirection: "column",
        minWidth: 220,
        boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
        borderRight: `2px solid ${currentTheme.colors.background}`,
      }}
    >
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
        {isEmpty ? (
          <div
            style={{
              padding: "16px",
              color: currentTheme.colors.secondaryTextColor,
              fontFamily: currentTheme.fonts.primary,
              fontSize: currentTheme.fontSizes.small,
            }}
          >
            No files or folders yet.
          </div>
        ) : (
          <>
            {rootFolders.map((node) => (
              <FolderRow
                key={node.id}
                node={node}
                depth={0}
                theme={currentTheme}
                highlightedIds={highlightedIds}
                setHighlight={setHighlightedIds}
              />
            ))}
            {rootFiles.map((file) => (
              <FileRow
                key={file.fileId}
                file={file}
                depth={0}
                theme={currentTheme}
                highlightedIds={highlightedIds}
                setHighlight={setHighlightedIds}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default InputsSidePanel;
